import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ArrowRight, Loader2 } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { cn } from '@shared/utils/cn';
import { useTenant } from '@core/tenant';
import type { Reservation } from '../hooks/useUserReservations';
import { userReservationsKeys } from '../hooks/useUserReservations';
import {
  checkModifyTimePolicy,
  fetchAvailableTimesForEdition,
  submitTimeChange,
} from '../api/reservationApi';
import type { AvailableTimeSlot } from '../api/reservationApi';

type Step = 'loading' | 'choose' | 'confirm';

interface ChangeTimeViewProps {
  reservation: Reservation;
  onClose: () => void;
  onSuccess: () => void;
}

function parseWithoutTimezone(isoStr: string): Date {
  const stripped = isoStr.slice(0, 19);
  const [datePart, timePart] = stripped.split('T');
  const [y, m, d] = datePart.split('-').map(Number);
  if (!timePart) return new Date(y, m - 1, d, 12, 0, 0);
  const [h, min, s] = timePart.split(':').map(Number);
  return new Date(y, m - 1, d, h, min, s);
}

function formatTime(timeStr: string): string {
  const date = parseWithoutTimezone(timeStr);
  return date
    .toLocaleTimeString('en', { hour: 'numeric', minute: '2-digit', hour12: true })
    .toUpperCase();
}

const ChangeTimeView = ({ reservation, onClose, onSuccess }: ChangeTimeViewProps) => {
  const { t } = useTranslation('profile');
  const { subdomainPrefix, restaurant } = useTenant();
  const queryClient = useQueryClient();

  const [step, setStep] = useState<Step>('loading');
  const [slots, setSlots] = useState<AvailableTimeSlot[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<AvailableTimeSlot | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const currentTime = formatTime(reservation.resTime);

  useEffect(() => {
    let cancelled = false;

    async function init() {
      try {
        const policy = await checkModifyTimePolicy(reservation.id);
        if (cancelled) return;

        if (!policy.canModify) {
          setError(t('MODIFY_RESERVATION_STRING_13_POLICY'));
          setStep('choose');
          return;
        }

        if (!subdomainPrefix) {
          setError(t('MODIFY_RESERVATION_STRING_13_ERROR'));
          setStep('choose');
          return;
        }

        const available = await fetchAvailableTimesForEdition(
          subdomainPrefix,
          reservation.id,
        );
        if (cancelled) return;

        const visibleSlots = available.filter(
          (s) => s.available || s.restrictionTypes.includes('actual-reservation-time'),
        );

        if (visibleSlots.length === 0) {
          setError(t('MODIFY_RESERVATION_STRING_13_NO_SLOTS'));
          setStep('choose');
          return;
        }

        setSlots(visibleSlots);
        setStep('choose');
      } catch {
        if (!cancelled) {
          setError(t('MODIFY_RESERVATION_STRING_13_ERROR'));
          setStep('choose');
        }
      }
    }

    init();
    return () => { cancelled = true; };
  }, [reservation.id, subdomainPrefix, t]);

  const handleSelectSlot = (slot: AvailableTimeSlot) => {
    if (!slot.available) return;
    setSelectedSlot(slot);
    setStep('confirm');
  };

  const handleConfirm = async () => {
    if (!selectedSlot) return;
    setSubmitting(true);

    try {
      await submitTimeChange(reservation.id, selectedSlot.hour, selectedSlot.areaId);

      if (restaurant?.id) {
        await queryClient.invalidateQueries({
          queryKey: userReservationsKeys.byRestaurant(restaurant.id),
        });
      }

      onSuccess();
    } catch {
      setSubmitting(false);
      setError(t('MODIFY_RESERVATION_STRING_13_ERROR'));
      setStep('choose');
      setSelectedSlot(null);
    }
  };

  const handleBack = () => {
    if (step === 'confirm') {
      setSelectedSlot(null);
      setStep('choose');
    } else {
      onClose();
    }
  };

  if (step === 'loading') {
    return (
      <div className="w-full flex flex-col items-center py-12">
        <Loader2 size={24} className="animate-spin text-brand" />
      </div>
    );
  }

  if (step === 'confirm' && selectedSlot) {
    return (
      <div className="w-full flex flex-col items-center">
        <h2 className="text-3xl font-display mb-8">{t('MODIFY_RESERVATION_STRING_13_CONFIRM_TITLE')}</h2>

        <div className="flex items-center gap-4 mb-6">
          <div className="text-center">
            <span className="block text-sm text-foreground/50 mb-1">{t('MODIFY_RESERVATION_STRING_13_CURRENT')}</span>
            <span className="text-lg font-semibold text-brand-dark">{currentTime}</span>
          </div>
          <ArrowRight size={20} className="text-foreground/40 mt-4" />
          <div className="text-center">
            <span className="block text-sm text-foreground/50 mb-1">&nbsp;</span>
            <span className="text-lg font-semibold text-brand-dark">{selectedSlot.hour}</span>
          </div>
        </div>

        <p className="text-sm text-foreground/70 text-center mb-8 max-w-xs">
          {t('MODIFY_RESERVATION_STRING_13_CONFIRM_MSG')}
        </p>

        <div className="w-full max-w-xs space-y-3">
          <button
            className="w-full py-3 rounded-full bg-brand-dark text-brand-foreground font-medium text-sm tracking-wide hover:bg-brand transition-colors disabled:opacity-50"
            onClick={handleConfirm}
            disabled={submitting}
          >
            {submitting ? (
              <Loader2 size={18} className="animate-spin mx-auto" />
            ) : (
              t('MODIFY_RESERVATION_STRING_13_CONFIRM')
            )}
          </button>
          <button
            className="w-full py-3 rounded-full border border-border text-brand-dark font-medium text-sm tracking-wide hover:bg-foreground/5 transition-colors"
            onClick={handleBack}
            disabled={submitting}
          >
            {t('MODIFY_RESERVATION_STRING_13_BACK')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col items-center">
      <div className="w-full flex items-center mb-8">
        <button
          className="p-2 -ml-2 text-brand-dark hover:text-brand transition-colors"
          onClick={handleBack}
          aria-label={t('MODIFY_RESERVATION_STRING_13_BACK')}
        >
          <ArrowLeft size={20} />
        </button>
        <h2 className="text-3xl font-display flex-1 text-center pr-8">
          {t('MODIFY_RESERVATION_STRING_13_TITLE')}
        </h2>
      </div>

      {error ? (
        <p className="text-red-500 text-sm text-center mb-6">{error}</p>
      ) : null}

      {slots.length > 0 && (
        <div className="w-full grid grid-cols-5 gap-2">
          {slots.map((slot) => {
            const isCurrent = slot.restrictionTypes.includes('actual-reservation-time');
            const isAvailable = slot.available && !isCurrent;

            return (
              <button
                key={slot.hour}
                className={cn(
                  'py-2.5 rounded-lg text-sm font-medium transition-colors',
                  isCurrent &&
                    'bg-brand-dark/10 text-brand-dark border border-brand-dark/30 cursor-default',
                  isAvailable &&
                    'bg-brand-dark text-brand-foreground hover:bg-brand cursor-pointer',
                  !isAvailable && !isCurrent &&
                    'bg-foreground/10 text-foreground/40 cursor-not-allowed',
                )}
                onClick={() => isAvailable && handleSelectSlot(slot)}
                disabled={!isAvailable}
              >
                {slot.hour}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ChangeTimeView;
